/**
 * Auth utilities — thin wrappers that delegate to the Redux store.
 * Use the Redux selectors directly in React components:
 *   import { selectIsAuthenticated, selectUser } from '../features/auth/authSlice';
 *
 * These helpers are kept for backwards compatibility with non-component code.
 */

let _store = null;

export function injectAuthStore(store) {
    _store = store;
}

function getAuthState() {
    return _store ? _store.getState().auth : { token: null, user: null };
}

export const isAuthenticated = () => {
    return !!getAuthState().token;
};

export const getUser = () => {
    return getAuthState().user;
};

export const getToken = () => {
    return getAuthState().token;
};

export const logout = () => {
    if (_store) {
        import('../features/auth/authSlice').then(({ logoutSession }) => {
            _store.dispatch(logoutSession());
        });
    }
};

export const hasRole = (role) => {
    const user = getUser();
    return user?.role === role;
};

export const hasAnyRole = (roles) => {
    const user = getUser();
    return user ? roles.includes(user.role) : false;
};

// UX-only permission mirror of backend/src/security/permissions.js.
// Server authorization is authoritative; these helpers only decide which
// controls to render. Keep entries minimal and aligned with the backend.
const ROLE_PERMISSIONS = {
    super_admin: ['operations:execute', 'operations:privileged', 'policies:read', 'policies:write'],
    admin: ['operations:execute', 'operations:privileged', 'policies:read', 'policies:write'],
    operator: ['operations:execute', 'policies:read'],
    viewer: ['policies:read']
};

export const hasPermission = (permission, user = getUser()) => {
    if (!user) return false;
    return (ROLE_PERMISSIONS[user.role] || []).includes(permission);
};

export const isTokenExpired = () => {
    return !getAuthState().token;
};
